import React, { useState, useEffect, useContext } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { TemplateContext } from '../../layout';
import { useForm, Controller } from 'react-hook-form';
import WithReach from '../withReach';
import CountryField from '../inputs/CountryField';
import RegionField from '../inputs/RegionField';
import TextField from '../inputs/TextField';
import PhoneField from '../inputs/PhoneField';
import { validationSchema } from '../../func/validation';
import CardField from '../inputs/cardField';
import Section from '../Section';
import scrollIntoView from '../../func/scrollIntoView';
import Button from '../buttons/Button';
import Checkbox from '../inputs/Checkbox';
import PaypalButton from '../buttons/paypalButton';
import CardCvc from '../inputs/cardCvc';
import Accordion from '../accordion';
import questionIcon from '/fallbacks/question-card.svg';
import lockIcon from '/fallbacks/lock-2.svg';
import PaymentsIcon from '/fallbacks/paymentColor.svg';
import PaymentsIconColored from '/fallbacks/secure-payment-logos-color.svg';
import LockSvg from '/fallbacks/encryptedLock.svg';
import browserIcon from '/fallbacks/browser.svg';
import paypalIcon from '/fallbacks/paypal-new.svg';
import shieldIcon from '/fallbacks/shield.svg';
import PersonIcon from '/fallbacks/person-profile.svg';
import DeliveryIcon from '/fallbacks/fast-delivery.svg';
import Expiration from '../inputs/Expiration';

const FormAccordions = ({
	labels,
	headings,
	handleFormSubmit,
	formSubmited,
	initialValues,
	cleanVersion,
	compliance,
	reachData,
}) => {
	const [templateContext, setTemplateContext] = useContext(TemplateContext);
	const [country, setCountry] = useState();
	const base = window.post_template?.checkout_v3_form;
	const formOrder = base?.checkout_v3_payment_method_position;
	const sslText = base.tr_checkout_v3_formEncrypted;
	const merchantId = window.general_settings?.merchant_id?.length > 0;
  	const isAdyenEnabled = window?.isAdyenEnabled;

	// icons && fallbacks

	const paymentsColoredIcon = base.checkout_v3_paymentsAfter ? base.checkout_v3_paymentsAfter : PaymentsIconColored;
	const paypalInnerIcon = headings.checkout_v3_form_paypalInnerIcon
		? headings.checkout_v3_form_paypalInnerIcon
		: browserIcon;
	const paypalAccordionIcon = headings.checkout_v3_form_paypalCollapseIcon
		? headings.checkout_v3_form_paypalCollapseIcon
		: paypalIcon;
	const cvcIcon =
		labels.checkout_v3_form_securityCom && labels.tr_checkout_v3_form_securityIcon
			? labels.tr_checkout_v3_form_securityIcon
			: questionIcon;
	const informationSectionIcon = headings.checkout_v3_form_informationImage
		? headings.checkout_v3_form_informationImage
		: PersonIcon;
	const paymentMethodsIcon = headings.checkout_v3_form_methodsicon
		? headings.checkout_v3_form_methodsicon
		: shieldIcon;
	const shippingSectionIcon = headings.checkout_v3_form_shippingIcon
		? headings.checkout_v3_form_shippingIcon
		: DeliveryIcon;

	const directAffiliate = window?.affiliate.includes('-direct');
	const reachOnlyDirectGlobal = window.reach_global?.reach_direct && directAffiliate;
	const reachOnlyIfDirectLocal = window.post_template?.checkout_v3_reach?.checkout_v3_reach_direct && directAffiliate;
	const reachLocal = window.post_template?.checkout_v3_reach?.checkout_v3_reach_toggle;
	const reachGlobal = window.reach_global.reach_global;
	const displayGlobalBadge = (reachOnlyDirectGlobal || reachGlobal) && !reachLocal && !reachOnlyIfDirectLocal;
	const displayLocalBadge = reachLocal || reachOnlyIfDirectLocal;
	const showTermsBox = cleanVersion || compliance || directAffiliate;

	const {
		register,
		setValue,
		handleSubmit,
		control,
		getValues,
		trigger,
		watch,
		formState: { errors, submitCount },
	} = useForm({
		defaultValues: initialValues,
		resolver: yupResolver(validationSchema),
		context: { type: { method: templateContext?.paymentMethod, safe: !!showTermsBox } },
	});

	useEffect(() => {
		scrollIntoView(errors);
	}, [errors]);

	useEffect(() => {
		const { country } = getValues();
		if (country) {
			setCountry(country);
		}
	}, []);

	const formNames = watch(['expiration']);

	useEffect(() => {
		if (formNames[0] && formNames[0].length > 3) {
			const splitedValue = formNames[0].split('/');
			splitedValue[0] && setValue('card_exp_month', splitedValue[0]);
			splitedValue[1] && setValue('card_exp_year', `20${splitedValue[1]}`);
		}
	}, [formNames]);

	return (
		<form
			className="paymentMethodNew"
			autocomplete="on"
			onSubmit={handleSubmit((values) => {
				handleFormSubmit(values, templateContext);
			})}
		>
			<div className="form-order" style={{ display: 'grid', flexDirection: 'column' }}>
				<Section
					heading={headings.tr_checkout_v3_form_informationHeading}
					orderType={formOrder}
					paddings={false}
					margin={false}
					mobileBorders={true}
					narrowGap={true}
					withBorder={false}
					order={formOrder == 'top' ? 2 : 1}
					icon={informationSectionIcon}
				>
					<Controller
						control={control}
						name="email"
						render={({ field: { onChange, value } }) => (
							<TextField
								type="email"
								placeholder={labels.tr_checkout_v3_form_emailLabel}
								name="email"
								width="full"
								error={errors.email ? errors.email.message : null}
								register={register}
								value={value}
								handleChange={onChange}
							/>
						)}
					/>
					<Controller
						control={control}
						name="phone"
						render={({ field: { onChange, value } }) => (
							<PhoneField
								value={value}
								error={errors.phone ? errors.phone.message : null}
								handleChange={onChange}
							/>
						)}
					></Controller>
				</Section>
				<Section
					heading={headings.tr_checkout_v3_form_shippingHeading}
					order={formOrder == 'top' ? 3 : 2}
					orderType={formOrder}
					paddings={false}
					mobileBorders={true}
					narrowGap={true}
					withBorder={false}
					icon={shippingSectionIcon}
					iconOffset={-1}
				>
					<Controller
						control={control}
						name="first_name"
						render={({ field: { onChange, value } }) => (
							<TextField
								type="text"
								placeholder={labels.tr_checkout_v3_form_nameLabel}
								name="first_name"
								width="half"
								margin="true"
								autoComplete="cc-given-name"
								value={value}
								handleChange={onChange}
								error={errors.first_name ? errors.first_name?.message : null}
							/>
						)}
					/>
					<Controller
						control={control}
						name="last_name"
						render={({ field: { onChange, value } }) => (
							<TextField
								type="text"
								placeholder={labels.tr_checkout_v3_form_lastNameLabel}
								name="last_name"
								autoComplete="cc-family-name"
								error={errors.last_name ? errors.last_name?.message : null}
								width="half"
								value={value}
								handleChange={onChange}
							/>
						)}
					/>
					<Controller
						control={control}
						name="address"
						render={({ field: { onChange, value } }) => (
							<TextField
								type="text"
								placeholder={labels.tr_checkout_v3_form_addressLabel}
								error={errors.address ? errors.address.message : null}
								name="address"
								autoComplete="street-address"
								width="full"
								value={value}
								handleChange={onChange}
							/>
						)}
					/>
					<Controller
						control={control}
						name="country"
						render={({ field: { onChange, value } }) => (
							<CountryField
								label={labels.tr_checkout_v3_form_countryLabel}
								name="countvry"
								error={errors.country ? errors.country.message : null}
								value={value}
								handleChange={(e) => {
									onChange(e);
									setCountry(e);
								}}
							/>
						)}
					/>

					<Controller
						control={control}
						name="city"
						render={({ field: { onChange, value } }) => (
							<TextField
								type="text"
								placeholder={labels.tr_checkout_v3_form_cityLabel}
								name="city"
								width="half"
								autoComplete="address-level2"
								error={errors.city ? errors.city.message : null}
								value={value}
								handleChange={onChange}
							/>
						)}
					/>
					<Controller
						control={control}
						name="state"
						render={({ field: { onChange, value } }) => (
							<RegionField
								label={labels.tr_checkout_v3_form_stateLabel}
								error={errors.state ? errors.state.message : null}
								country={country || window.countryCode.toUpperCase()}
								handleChange={onChange}
								value={value}
							/>
						)}
					/>
					<Controller
						control={control}
						name="zip"
						render={({ field: { onChange, value } }) => (
							<TextField
								type="text"
								placeholder={labels.tr_checkout_v3_form_postLabel}
								error={errors.zip ? errors.zip.message : null}
								name="zip"
								width="half"
								autoComplete="postal-code"
								value={value}
								handleChange={onChange}
							/>
						)}
					/>
				</Section>
				<Section
					heading={headings.checkout_v3_form_methodsHeading}
					order={formOrder == 'top' ? 1 : 3}
					orderType={formOrder}
					paddings={false}
					withBorder={false}
					narrowGap={true}
					icon={paymentMethodsIcon}
				>
					<div className="accordionContainer" style={{ marginBottom: formOrder == 'bottom' ? '16px' : '0px' }}>
						<Accordion
							label={headings.tr_checkout_v3_form_cardCollapseHeading}
							image={
								headings.checkout_v3_form_cardCollapseIcon
									? headings.checkout_v3_form_cardCollapseIcon
									: PaymentsIcon
							}
							name="card"
							defaultState={true}
							overflow={templateContext?.paymentMethod == 'card' ? true : null}
						>
							<Controller
								control={control}
								name="card_number"
								render={({ field: { onChange, value } }) => (
									<CardField
										name="card_number"
										icon={lockIcon}
										comment={labels.tr_checkout_v3_form_cardComment}
										width="full"
										placeholder={labels.tr_checkout_v3_form_cardNumberLabel}
										error={errors.card_number ? errors.card_number.message : null}
										value={value}
										handleChange={onChange}
										submitCount={submitCount}
									/>
								)}
							/>
							<Controller
								control={control}
								name="expiration"
								render={({ field: { onChange, value } }) => (
									<Expiration
										type="tel"
										name="expiration"
										width={67}
										max={5}
										placeholder={labels.tr_checkout_v3_form_expirationLabel}
										margin={true}
										error={
											errors.card_exp_year || errors.card_exp_month
												? errors.card_exp_year?.message || errors.card_exp_month?.message
												: null
										}
										dataCy="expiration-input"
										register={register}
										value={value}
										trigger={trigger}
										handleChange={onChange}
									/>
								)}
							/>
							<Controller
								control={control}
								name="card_cvc"
								render={({ field: { onChange, value } }) => (
									<CardCvc
										type="tel"
										max={9999}
										min={1}
										name="card_cvc"
										submitCount={submitCount}
										icon={cvcIcon}
										comment={
											labels.checkout_v3_form_securityCom && labels.tr_checkout_v3_form_securityComment
										}
										width={33}
										setValue={setValue}
										placeholder={labels.tr_checkout_v3_form_securityLabel}
										error={errors.card_cvc ? errors.card_cvc?.message : null}
										value={value}
										handleChange={onChange}
									/>
								)}
							/>
						</Accordion>
						<Accordion
							defaultState={false}
							maxImgWidth={90}
							imageHeight={24}
							image={paypalAccordionIcon}
							name="paypal"
						>
							<span
								className="paypalRadio__image"
								style={{
									backgroundImage: `url('${paypalInnerIcon}')`,
									margin: '0 auto',
									marginBottom: '16px',
								}}
							></span>
							<p className="paypalRadio__text">{headings.tr_checkout_v3_form_paypalInner}</p>
						</Accordion>
					</div>
					{(displayLocalBadge || displayGlobalBadge) && merchantId && (
						<div className="with-reach-container">
							<WithReach
								environment="local"
								type="accordion"
								onlyDirectGlobal={reachOnlyDirectGlobal}
								onlyDirectLocal={reachOnlyIfDirectLocal}
								text={reachData?.Html}
								image={reachData?.ImageUrl}
							/>
						</div>
					)}
				</Section>
			</div>
			{showTermsBox && (
				<div style={{ marginBottom: '16px' }}>
					<Controller
						control={control}
						name="policy"
						render={({ field: { onChange, value } }) => (
							<Checkbox
								error={errors.policy ? errors.policy.message : null}
								value={value}
								handleChange={onChange}
								name="policy"
							/>
						)}
					/>
				</div>
			)}

			{templateContext?.paymentMethod == 'card' ? (
				<Button
					id="hook-form"
					type="submit"
					margin={cleanVersion ? 16 : 4}
					text={base?.tr_checkout_v3_form_cta}
					processingLabel={base?.tr_checkout_v3_form_processing}
					loading={formSubmited}
				/>
			) : (
				<PaypalButton
					id="hook-form"
					text={base?.tr_checkout_v3_form_paypalText}
					loading={formSubmited}
					formNoValidate={false}
					type="submit"
					margin={cleanVersion ? 16 : 0}
				/>
			)}
			<div className="paymentIcons">
				<div className="paymentIcons__text">
					<span
						style={{
							backgroundImage: `url(${LockSvg})`,
						}}
					></span>
					<span>{sslText}</span>
				</div>
				<img className="paymentIcons__icons" src={`${paymentsColoredIcon}`} alt="Payments" />
			</div>
			{isAdyenEnabled && <input type="hidden" name="browserData" value="" id="browserData" />}
		</form>
	);
};

export default FormAccordions;
